import { addDays } from 'date-fns';
import {
	GOALS_OF_COVER_LABELS,
	NEEDS_ANALYSIS_LABELS,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';

export const normalizeRecipient = (contact) => {
	const contactEmail = contact?.contactEmail ?? contact?.emailAddress ?? null;
	if (!contactEmail) return null;
	return {
		id: contact?.id ?? contact?.hubspotId ?? contactEmail,
		contactName:
			contact?.contactName ||
			contact?.name ||
			[contact?.contactFirstName, contact?.contactLastName].filter(Boolean).join(' ') ||
			contactEmail,
		contactEmail,
	};
};

export const mergeRecipientsByEmail = (currentRecipients, additionalRecipient) => {
	const normalizedAdditional = normalizeRecipient(additionalRecipient);
	if (!normalizedAdditional) return currentRecipients ?? [];

	const nextRecipients = Array.isArray(currentRecipients) ? [...currentRecipients] : [];
	const additionalEmail = normalizedAdditional.contactEmail.toLowerCase();
	const alreadyExists = nextRecipients.some(
		(recipient) => (recipient?.contactEmail ?? '').toLowerCase() === additionalEmail,
	);

	if (!alreadyExists) {
		nextRecipients.push(normalizedAdditional);
	}

	return nextRecipients;
};

export const buildUnlayerUserContext = ({ selectedFrom, userDetails, fallbackEmail }) => ({
	name: userDetails?.name ?? '[User Name]',
	phoneNumber: userDetails?.phoneNumber ?? '',
	username:
		selectedFrom?.value ??
		selectedFrom?.email ??
		selectedFrom?.name ??
		userDetails?.displayEmail ??
		fallbackEmail,
	aboutUser: userDetails?.aboutUser ?? userDetails?.about ?? userDetails?.role ?? '',
	imageUrl: userDetails?.imageUrl ?? '',
	disclosureUrl: userDetails?.disclosureUrl ?? '',
	linkedIn: userDetails?.linkedIn ?? '',
	fspNumber: userDetails?.fspNumber ?? '',
	signature: userDetails?.signature ?? '',
	// Keep legacy aliases for existing callers while merge tags use the new field names above.
	email:
		selectedFrom?.value ??
		selectedFrom?.email ??
		selectedFrom?.name ??
		userDetails?.displayEmail ??
		fallbackEmail,
	about: userDetails?.aboutUser ?? userDetails?.about ?? userDetails?.role ?? '',
	broker: userDetails?.broker,
});

export const getIndustryLabel = ({ industryCode, industryResults = [] }) => {
	if (!industryCode) return industryCode ?? '';
	const matchedIndustry = (industryResults ?? []).find(
		(item) => item?.Value?.toString() === industryCode.toString(),
	);
	return matchedIndustry?.Label ?? industryCode;
};

const pickClientField = (...values) =>
	values.find(
		(value) =>
			value !== undefined &&
			value !== null &&
			!(typeof value === 'string' && value.trim() === ''),
	);

export const buildUnlayerClientContext = ({ clientDetails, industryResults = [] }) => {
	if (!clientDetails || typeof clientDetails !== 'object') {
		return clientDetails ?? null;
	}

	const clientRow = clientDetails?.clientRow ?? {};
	const insight = clientDetails?.insight ?? {};

	const industryCode = pickClientField(clientDetails?.industry, clientRow?.industry);
	const numberOfEmployees = clientRow?.numberOfEmployees;
	const annualRevenue = clientRow?.annualRevenue;

	return {
		...clientDetails,
		...clientRow,
		...insight,
		numberOfEmployees,
		annualRevenue,
		industry: getIndustryLabel({
			industryCode,
			industryResults,
		}),

		addressLine1: insight?.AddressLine1 ?? '',
		addressLine2: insight?.AddressLine2 ?? '',
		addressLine3: insight?.AddressLine3 ?? '',
		suburb: insight?.Suburb ?? '',
		state: insight?.StateName ?? '',
		city: insight?.City ?? '',
		postcode: insight?.Postcode ?? '',
		country: insight?.CountryName ?? '',
	};
};

export const buildUnlayerDealContext = ({ dealDetails, classesOfRisk = [], policies = [] }) => {
	if (!dealDetails || typeof dealDetails !== 'object') {
		return dealDetails ?? null;
	}

	const classOfRiskLabelMap = new Map(
		(classesOfRisk ?? [])
			.map((item) => [item?.classOfRisk, item?.label])
			.filter(([classOfRisk]) => Boolean(classOfRisk)),
	);
	const goalsOfCoverValues = Array.isArray(dealDetails?.goalsOfCover)
		? dealDetails.goalsOfCover
				.map((goalCode) => GOALS_OF_COVER_LABELS[goalCode] ?? goalCode)
				.filter(Boolean)
		: [];
	const additionalGoalsOfCover = (dealDetails?.additionalGoalsOfCover ?? '').trim();
	const normalizedGoalsOfCover = additionalGoalsOfCover
		? [...goalsOfCoverValues, additionalGoalsOfCover]
		: goalsOfCoverValues;
	const normalizedRiskClasses = Array.isArray(dealDetails?.riskClasses)
		? dealDetails.riskClasses
				.map((riskClass) => {
					const riskClassKey =
						typeof riskClass === 'string'
							? riskClass
							: (riskClass?.classOfRisk ?? riskClass?.key ?? riskClass?.value ?? '');
					return classOfRiskLabelMap.get(riskClassKey) ?? riskClassKey;
				})
				.filter(Boolean)
		: [];
	const normalizedNeedsAnalysis = Array.isArray(dealDetails?.needsAnalysis)
		? dealDetails.needsAnalysis
				.map((needsItem) => {
					const needsKey =
						needsItem?.key ?? needsItem?.classOfRisk ?? needsItem?.riskClass ?? '';
					const needsValue = needsItem?.value ?? '';
					const keyLabel = classOfRiskLabelMap.get(needsKey) ?? needsKey;
					const valueLabel = NEEDS_ANALYSIS_LABELS[needsValue] ?? needsValue;
					if (!keyLabel || !valueLabel) {
						return null;
					}
					return {
						...needsItem,
						key: keyLabel,
						value: valueLabel,
					};
				})
				.filter(Boolean)
		: [];

	return {
		...dealDetails,
		goalsOfCover: normalizedGoalsOfCover,
		riskClasses: normalizedRiskClasses,
		needsAnalysis: normalizedNeedsAnalysis,
		policies,
	};
};

export const getInitialComposeValues = ({
	presetClient,
	presetDeal,
	presetRecipients,
	presetSubject = '',
	presetEditorHtml = '',
	presetEditorDesign = null,
}) => ({
	client: presetClient ?? null,
	deal: presetDeal ?? null,
	from: null,
	to: presetRecipients,
	bcc: [],
	cc: [],
	subject: presetSubject,
	files: [],
	editorHtml: presetEditorHtml,
	editorDesign: presetEditorDesign,
	selectedFolder: '',
	driveId: '',
	createFollowUpTask: false,
	followUpDueDate: addDays(new Date(), 3),
});

export const getEmptyUnlayerDesign = () => ({
	counters: {},
	body: {
		rows: [],
		values: {
			backgroundColor: '#FFFFFF',
			contentWidth: '600px',
			fontFamily: {
				label: 'Arial',
				value: 'Arial, Helvetica, sans-serif',
			},
		},
	},
});

export const getDesignFingerprint = (design) => {
	try {
		return JSON.stringify(design ?? null);
	} catch (error) {
		return '';
	}
};

export const formatDraftTimestamp = (value) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const datePart = date.toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short',
	});
	const timePart = date.toLocaleTimeString('en-GB', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
	return `[${datePart} ${timePart}]`;
};
